import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Mail, Link2, Copy, Trash2, Pencil, Plus, Crown, X, Trophy, GripVertical, Send, CopyPlus, BellRing, Search, Upload, Youtube, RotateCcw, Download, KeyRound } from "lucide-react";
import { InviteCodesSection, TempPasswordModal } from "../components/InviteCodesSection";
import { ObjectifField } from "../components/ObjectifField";
import { Reorder } from "framer-motion";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../context/AuthContext";

const JOURS = ["Jour 1", "Jour 2", "Jour 3", "Jour 4"];
const EMPTY_ROW = { bloc: "A", jour: "Jour 1", groupe: "", type: "Superset", ordre: 1, exercice: "", muscle: "", equipement: "", series: 4, objectif: "RIR 1-2 (8-12 reps)", tempo: "3-0-1-0", repos_s: 90 };

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [weekly, setWeekly] = useState(null);
  const [sending, setSending] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [remindBusy, setRemindBusy] = useState(false);
  const [streakBusy, setStreakBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/admin/activity").then(({ data }) => setActivity(data));
    api.get("/admin/weekly-summary").then(({ data }) => setWeekly(data));
    api.get("/admin/reminders").then(({ data }) => setReminders(data));
  }, []);

  const sendReminders = async () => {
    setRemindBusy(true);
    try {
      const { data } = await api.post("/admin/reminders/send");
      toast.success(`${data.sent} rappel${data.sent > 1 ? "s" : ""} envoyé${data.sent > 1 ? "s" : ""}`);
      const failed = data.details.filter((d) => d.status.startsWith("échec"));
      if (failed.length > 0) toast.warning(`${failed.length} échec(s) — adresses non vérifiées sur Resend`);
      api.get("/admin/reminders").then(({ data: d }) => setReminders(d));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRemindBusy(false);
    }
  };

  const sendStreakReminders = async () => {
    setStreakBusy(true);
    try {
      const { data } = await api.post("/admin/streak-reminders/send");
      toast.success(`${data.sent} rappel${data.sent > 1 ? "s" : ""} de streak envoyé${data.sent > 1 ? "s" : ""}`);
      const failed = (data.details || []).filter((d) => d.status.startsWith("échec"));
      if (failed.length > 0) toast.warning(`${failed.length} échec(s) — adresses non vérifiées sur Resend`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setStreakBusy(false);
    }
  };

  const sendWeekly = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/weekly-summary/send");
      toast.success(`Résumé envoyé à ${data.sent_to}`);
      setWeekly({ last_sent: data.last_sent });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const exportFull = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/admin/export-full", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gymtrack_export_complet_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export complet téléchargé !");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de l'export");
    } finally {
      setExporting(false);
    }
  };

  if (!stats) return null;
  return (
    <div className="space-y-4" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 gap-px bg-white/10 border border-[#2E2E3A]">
        {[["Membres", stats.nb_users], ["Séries loggées", stats.nb_sets], ["Séances", stats.nb_sessions], ["Exercices au programme", stats.nb_exercices]].map(([l, v]) => (
          <div key={l} className="bg-[#14141A] p-4">
            <p className="font-display text-3xl text-white">{v}</p>
            <p className="text-[#B0B0B4] text-[10px] uppercase tracking-wider">{l}</p>
          </div>
        ))}
      </div>
      {stats.top_user && (
        <div className="bg-[#14141A] border border-[#2E2E3A] p-4 flex items-center gap-3">
          <Crown size={20} className="text-amber-400" />
          <div>
            <p className="text-white text-sm font-medium">{stats.top_user.name}</p>
            <p className="text-[#B0B0B4] text-xs">Membre le plus actif · {Math.round(stats.top_user.volume)} lb de volume total</p>
          </div>
        </div>
      )}
      <div className="bg-[#14141A] border border-[#2E2E3A] p-4">
        <p className="text-[#B0B0B4] text-xs">{stats.invites_pending} invitation{stats.invites_pending > 1 ? "s" : ""} en attente</p>
      </div>
      <div className="bg-[#14141A] border border-[#2E2E3A] p-4" data-testid="reminders-card">
        <p className="font-display text-xl text-white">RAPPELS DE SÉANCE</p>
        <p className="text-[#B0B0B4] text-xs mt-1">Courriel automatique aux membres inactifs depuis 3 jours ou plus (vérification 2×/jour).</p>
        <div className="mt-2 divide-y divide-white/5">
          {reminders.filter((r) => r.needs_reminder).length === 0 ? (
            <p className="text-[#32D74B] text-xs py-1">Tout le monde est actif 💪</p>
          ) : (
            reminders.filter((r) => r.needs_reminder).map((r) => (
              <div key={r.email} className="py-1.5 flex items-center justify-between text-xs">
                <span className="text-white">{r.name}</span>
                <span className="text-[#FF3B30]">{r.days_inactive} jours d'inactivité{r.last_reminder ? ` · rappelé le ${r.last_reminder}` : ""}</span>
              </div>
            ))
          )}
        </div>
        {reminders.some((r) => r.needs_reminder) && (
          <button data-testid="send-reminders-button" disabled={remindBusy} onClick={sendReminders}
            className="mt-3 h-10 px-4 border border-[#FF3B30] text-[#FF3B30] text-sm font-semibold flex items-center gap-2 hover:bg-[#FF3B30] hover:text-white transition-colors disabled:opacity-40">
            <BellRing size={15} /> {remindBusy ? "Envoi..." : "Envoyer les rappels maintenant"}
          </button>
        )}
      </div>
      <div className="bg-[#14141A] border border-[#2E2E3A] p-4" data-testid="streak-reminders-card">
        <p className="font-display text-xl text-white">RAPPELS DE STREAK</p>
        <p className="text-[#B0B0B4] text-xs mt-1">Chaque vendredi, courriel automatique aux membres sans séance dans la semaine pour sauver leur flamme 🔥 (1 envoi max/semaine).</p>
        <button data-testid="send-streak-reminders-button" disabled={streakBusy} onClick={sendStreakReminders}
          className="mt-3 h-10 px-4 border border-[#FF3B30] text-[#FF3B30] text-sm font-semibold flex items-center gap-2 hover:bg-[#FF3B30] hover:text-white transition-colors disabled:opacity-40">
          <BellRing size={15} /> {streakBusy ? "Envoi..." : "Envoyer maintenant"}
        </button>
      </div>
      <div className="bg-[#14141A] border border-[#2E2E3A] p-4" data-testid="weekly-summary-card">
        <p className="font-display text-xl text-white">RÉSUMÉ HEBDOMADAIRE</p>
        <p className="text-[#B0B0B4] text-xs mt-1">
          Envoyé automatiquement chaque semaine par courriel (volume et records de l'équipe).
          {weekly?.last_sent && <span className="text-[#ccc]"> Dernier envoi : {weekly.last_sent.slice(0, 10)}</span>}
        </p>
        <button data-testid="send-weekly-button" disabled={sending} onClick={sendWeekly}
          className="mt-3 h-10 px-4 bg-[#FF3B30] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[#e0322a] transition-colors disabled:opacity-40">
          <Send size={15} /> {sending ? "Envoi..." : "Envoyer maintenant"}
        </button>
      </div>
      <div className="bg-[#14141A] border border-[#2E2E3A] p-4" data-testid="full-export-card">
        <p className="font-display text-xl text-white">EXPORT COMPLET</p>
        <p className="text-[#B0B0B4] text-xs mt-1">Toute la base en un fichier Excel : membres, séries, journaux, poids corporel, objectifs, duels, photos et invitations.</p>
        <button data-testid="full-export-button" disabled={exporting} onClick={exportFull}
          className="mt-3 h-10 px-4 bg-[#FF3B30] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[#e0322a] transition-colors disabled:opacity-40">
          <Download size={15} /> {exporting ? "Export..." : "Télécharger l'export Excel"}
        </button>
      </div>
      <div className="bg-[#14141A] border border-[#2E2E3A]" data-testid="admin-activity-feed">
        <p className="px-4 pt-3 pb-1 font-display text-xl text-white">ACTIVITÉ DES MEMBRES</p>
        <div className="divide-y divide-white/5">
          {activity.length === 0 && <p className="px-4 py-3 text-[#B0B0B4] text-sm">Aucune activité récente.</p>}
          {activity.map((a) => (
            <div key={a.id} className="px-4 py-2 flex items-center gap-2 text-sm">
              <div className="flex-1 min-w-0">
                <p className="text-white truncate"><span className="font-semibold">{a.name}</span> <span className="text-[#B0B0B4]">·</span> {a.exercice}</p>
                <p className="text-[#B0B0B4] text-[10px]">{a.date} · Bloc {a.bloc} · {a.jour}</p>
              </div>
              <span className="text-[#ccc] font-mono text-xs shrink-0">{a.poids ? `${a.poids} lb` : "PDC"} × {a.reps}</span>
              {a.prs?.length > 0 && <Trophy size={13} className="text-amber-400 shrink-0" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Invites = () => {
  const [email, setEmail] = useState("");
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/invites").then(({ data }) => setInvites(data)), []);
  useEffect(() => { load(); }, [load]);

  const copy = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Lien copié !");
    } catch (e) {
      toast.info(`Lien : ${link}`, { duration: 10000 });
    }
  };

  const create = async (sendEmail) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", {
        email: email || null, link_base: window.location.origin, send_email: sendEmail,
      });
      toast.success(sendEmail ? `Courriel envoyé à ${email}` : "Lien d'invitation créé");
      copy(data.link);
      setEmail("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    await api.delete(`/admin/invites/${id}`);
    toast.success("Invitation supprimée");
    load();
  };

  return (
    <div className="space-y-4">
      <div className="bg-[#14141A] border border-[#2E2E3A] p-4 space-y-2">
        <p className="font-display text-xl text-white">INVITER UN AMI</p>
        <input data-testid="invite-email-input" type="email" className="auth-input" placeholder="Courriel de ton ami (optionnel pour le lien)"
          value={email} onChange={(e) => setEmail(e.target.value)} />
        <div className="flex gap-2">
          <button data-testid="invite-send-email-button" disabled={busy || !email} onClick={() => create(true)}
            className="flex-1 h-11 bg-[#FF3B30] text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#e0322a] transition-colors disabled:opacity-30">
            <Mail size={16} /> Envoyer par courriel
          </button>
          <button data-testid="invite-create-link-button" disabled={busy} onClick={() => create(false)}
            className="flex-1 h-11 border border-white/15 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:border-[#FF3B30] transition-colors disabled:opacity-30">
            <Link2 size={16} /> Créer un lien
          </button>
        </div>
        <p className="text-[#666] text-[10px]">Le lien est copié automatiquement — partage-le par WhatsApp, SMS, etc.</p>
      </div>
      <div className="bg-[#14141A] border border-[#2E2E3A] divide-y divide-white/5" data-testid="invites-list">
        {invites.length === 0 && <p className="px-4 py-3 text-[#B0B0B4] text-sm">Aucune invitation pour l'instant.</p>}
        {invites.map((inv) => (
          <div key={inv.id} className="px-4 py-2.5 flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm truncate">{inv.email || "Lien partageable"}</p>
              <p className={`text-[10px] uppercase tracking-wider ${
                inv.status === "acceptée" ? "text-[#32D74B]" : inv.status === "expirée" ? "text-[#FF3B30]" : "text-[#B0B0B4]"}`}>
                {inv.status}{inv.accepted_by ? ` par ${inv.accepted_by}` : ""}
                {inv.status !== "acceptée" && inv.status !== "expirée" && inv.expires_at ? ` · expire le ${inv.expires_at.slice(0, 10)}` : ""}
              </p>
            </div>
            <button data-testid={`invite-copy-${inv.id}`} onClick={() => copy(`${window.location.origin}/invite/${inv.token}`)}
              className="text-[#B0B0B4] hover:text-white p-2 transition-colors"><Copy size={15} /></button>
            <button data-testid={`invite-delete-${inv.id}`} onClick={() => revoke(inv.id)}
              className="text-[#555] hover:text-[#FF3B30] p-2 transition-colors"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-wider text-[#B0B0B4]">{label}</span>
    {children}
  </label>
);

const RowForm = ({ initial, onSave, onClose, blocs = ["A", "B"] }) => {
  const [f, setF] = useState(initial);
  const [bank, setBank] = useState([]);
  const [custom, setCustom] = useState(false);
  const [newBloc, setNewBloc] = useState(false);
  useEffect(() => { api.get("/exercises/bank").then(({ data }) => setBank(data)).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const pickFromBank = (e) => {
    const name = e.target.value;
    if (name === "__custom__") { setCustom(true); return; }
    setCustom(false);
    const ex = bank.find((b) => b.exercice === name);
    if (ex) setF({ ...f, exercice: ex.exercice, muscle: ex.muscle, equipement: ex.equipement });
  };
  const muscles = [...new Set(bank.map((b) => b.muscle))];
  const inBank = bank.some((b) => b.exercice === f.exercice);
  const save = async () => {
    if (!f.exercice || !f.muscle) { toast.error("Nom de l'exercice et muscle requis"); return; }
    await onSave({ ...f, ordre: parseInt(f.ordre, 10) || 1, series: parseInt(f.series, 10) || 4, repos_s: parseInt(f.repos_s, 10) || 0 });
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="program-row-form" className="relative w-full max-w-md bg-[#14141A] border border-white/15 p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-3xl text-white">{initial.id ? "MODIFIER" : "AJOUTER"} UN EXERCICE</h2>
          <button data-testid="row-form-close" onClick={onClose} className="text-[#B0B0B4] hover:text-white p-2"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Bloc">
            {newBloc ? (
              <input data-testid="row-new-bloc" className="auth-input" placeholder="C" maxLength={3} autoFocus
                value={f.bloc} onChange={(e) => setF({ ...f, bloc: e.target.value.toUpperCase() })} />
            ) : (
              <select data-testid="row-bloc" className="auth-input" value={f.bloc}
                onChange={(e) => {
                  if (e.target.value === "__new__") { setNewBloc(true); setF({ ...f, bloc: "" }); }
                  else setF({ ...f, bloc: e.target.value });
                }}>
                {blocs.map((b) => <option key={b.bloc} value={b.bloc}>{b.bloc}{b.label ? ` · ${b.label}` : ""}</option>)}
                <option value="__new__">➕ Nouveau bloc…</option>
              </select>
            )}
          </Field>
          <Field label="Séance">
            <select data-testid="row-jour" className="auth-input" value={f.jour} onChange={set("jour")}>
              {JOURS.map((j) => <option key={j}>{j}</option>)}
            </select>
          </Field>
          <Field label="Groupe (ex: Superset 1)"><input data-testid="row-groupe" className="auth-input" value={f.groupe} onChange={set("groupe")} /></Field>
          <Field label="Type">
            <select data-testid="row-type" className="auth-input" value={f.type} onChange={set("type")}>
              {["Superset", "Giant set", "Séries droites", "Échauffement", "Étirements", "Cardio"].map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Ordre"><input data-testid="row-ordre" type="number" className="auth-input" value={f.ordre} onChange={set("ordre")} /></Field>
          <Field label="Séries"><input data-testid="row-series" type="number" className="auth-input" value={f.series} onChange={set("series")} /></Field>
        </div>
        <div className="space-y-2 mt-2">
          <Field label="Exercice — choisis dans la banque (muscle + équipement auto-remplis)">
            <select data-testid="row-exercice-bank" className="auth-input"
              value={custom || !inBank ? "__custom__" : f.exercice} onChange={pickFromBank}>
              <option value="__custom__">✏️ Saisie libre / nouvel exercice…</option>
              {muscles.map((m) => (
                <optgroup key={m} label={m}>
                  {bank.filter((b) => b.muscle === m).map((b) => (
                    <option key={b.id} value={b.exercice}>{b.exercice} · {b.equipement}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>
          {(custom || !inBank) && (
            <Field label="Nom de l'exercice"><input data-testid="row-exercice" className="auth-input" value={f.exercice} onChange={set("exercice")} placeholder="Développé couché barre" /></Field>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Field label="Muscle"><input data-testid="row-muscle" className="auth-input" value={f.muscle} onChange={set("muscle")} placeholder="Pectoraux" /></Field>
            <Field label="Équipement"><input data-testid="row-equipement" className="auth-input" value={f.equipement} onChange={set("equipement")} placeholder="Barre" /></Field>
          </div>
          <Field label="Objectif (RIR / reps)">
            <ObjectifField value={f.objectif} onChange={(v) => setF((prev) => ({ ...prev, objectif: v }))} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Tempo"><input data-testid="row-tempo" className="auth-input" value={f.tempo} onChange={set("tempo")} /></Field>
            <Field label="Repos (s)"><input data-testid="row-repos" type="number" className="auth-input" value={f.repos_s} onChange={set("repos_s")} /></Field>
          </div>
        </div>
        <button data-testid="row-form-save" onClick={save}
          className="w-full h-12 mt-4 bg-[#FF3B30] text-white font-display text-2xl tracking-wide hover:bg-[#e0322a] transition-colors">
          ENREGISTRER
        </button>
      </div>
    </div>
  );
};

const ProgramManager = () => {
  const [bloc, setBloc] = useState("A");
  const [jour, setJour] = useState("Jour 1");
  const [rows, setRows] = useState([]);
  const [blocs, setBlocs] = useState([{ bloc: "A" }, { bloc: "B" }]);
  const [editing, setEditing] = useState(null);
  const [dup, setDup] = useState(null);
  const [backup, setBackup] = useState({ exists: false });
  const loadBackup = useCallback(() => api.get("/admin/program/backup").then(({ data }) => setBackup(data)).catch(() => {}), []);
  useEffect(() => { loadBackup(); }, [loadBackup]);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const loadBlocs = useCallback(() => api.get("/blocs").then(({ data }) => setBlocs(data)).catch(() => {}), []);

  const renameBloc = async () => {
    const current = blocs.find((b) => b.bloc === bloc);
    const label = window.prompt(`Nom du Bloc ${bloc} (ex: Force, Volume) — vide pour retirer :`, current?.label || "");
    if (label === null) return;
    await api.put(`/admin/blocs/${bloc}`, { label });
    toast.success(label ? `Bloc ${bloc} renommé « ${label} »` : `Nom du Bloc ${bloc} retiré`);
    loadBlocs();
  };

  const restoreBackup = async () => {
    if (!window.confirm("Restaurer le programme tel qu'il était avant le dernier import Excel ?")) return;
    try {
      const { data } = await api.post("/admin/program/restore");
      toast.success(`Programme restauré (${data.restored} exercices)`);
      load();
      loadBlocs();
      loadBackup();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const importExcel = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    toast.info("Import en cours…");
    try {
      const { data } = await api.post("/admin/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import réussi : ${data.programme} exos programme · ${data.banque} exos banque · ${data.journal} séries de journal${data.seances.length ? ` (${data.seances.length} séance${data.seances.length > 1 ? "s" : ""})` : ""}`);
      load();
      loadBlocs();
      loadBackup();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'import");
    }
  };
  const load = useCallback(() => api.get("/program", { params: { bloc, jour } }).then(({ data }) => setRows(data)), [bloc, jour]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadBlocs(); }, [loadBlocs]);

  const persistOrder = async () => {
    try {
      await api.post("/admin/program/reorder", { ids: rowsRef.current.map((r) => r.id) });
      toast.success("Ordre mis à jour");
    } catch (e) {
      toast.error("Erreur lors du réordonnancement");
      load();
    }
  };

  const save = async (data) => {
    try {
      if (editing.id) await api.put(`/admin/program/${editing.id}`, data);
      else await api.post("/admin/program", data);
      toast.success(editing.id ? "Exercice modifié" : "Exercice ajouté");
      setEditing(null);
      load();
      loadBlocs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/program/${id}`);
    toast.success("Exercice supprimé");
    load();
  };

  const duplicateDay = async () => {
    try {
      const { data } = await api.post("/admin/program/duplicate", {
        source_bloc: bloc, source_jour: jour, target_bloc: dup.target_bloc, target_jour: dup.target_jour,
      });
      toast.success(`${data.copied} exercices copiés vers Bloc ${dup.target_bloc} · ${dup.target_jour}`);
      setDup(null);
      loadBlocs();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        {blocs.map((b) => (
          <button key={b.bloc} data-testid={`admin-bloc-${b.bloc}`} onClick={() => setBloc(b.bloc)}
            className={`h-9 px-4 font-display text-lg whitespace-nowrap transition-colors ${bloc === b.bloc ? "bg-[#FF3B30] text-white" : "bg-[#14141A] text-[#B0B0B4]"}`}>
            BLOC {b.bloc}{b.label ? ` · ${b.label.toUpperCase()}` : ""}
          </button>
        ))}
        <button data-testid="rename-bloc-button" onClick={renameBloc}
          className="h-9 px-2.5 text-[#B0B0B4] hover:text-white border border-[#2E2E3A] shrink-0 transition-colors" aria-label="Renommer le bloc">
          <Pencil size={14} />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {JOURS.map((j) => (
          <button key={j} data-testid={`admin-jour-${j.slice(-1)}`} onClick={() => setJour(j)}
            className={`h-8 px-3 text-xs whitespace-nowrap border transition-colors ${jour === j ? "border-[#FF3B30] text-white bg-[#FF3B30]/10" : "border-[#2E2E3A] text-[#B0B0B4]"}`}>
            {j}
          </button>
        ))}
      </div>
      <button data-testid="add-exercise-button" onClick={() => setEditing({ ...EMPTY_ROW, bloc, jour, ordre: rows.length + 1 })}
        className="w-full h-11 border border-dashed border-white/20 text-[#B0B0B4] hover:text-white hover:border-[#FF3B30] text-sm flex items-center justify-center gap-2 transition-colors">
        <Plus size={16} /> Ajouter un exercice
      </button>
      <button data-testid="duplicate-day-button" onClick={() => setDup({ target_bloc: bloc === "A" ? "B" : "A", target_jour: jour })}
        className="w-full h-11 border border-dashed border-white/20 text-[#B0B0B4] hover:text-white hover:border-[#FF3B30] text-sm flex items-center justify-center gap-2 transition-colors">
        <CopyPlus size={16} /> Dupliquer ce jour vers...
      </button>
      <label data-testid="import-excel-label"
        className="w-full h-11 border border-dashed border-white/20 text-[#B0B0B4] hover:text-white hover:border-[#FF3B30] text-sm flex items-center justify-center gap-2 transition-colors cursor-pointer">
        <Upload size={16} /> Importer un fichier Excel (programme + banque + journal)
        <input data-testid="import-excel-input" type="file" accept=".xlsx" className="hidden" onChange={importExcel} />
      </label>
      {backup.exists && (
        <button data-testid="restore-program-button" onClick={restoreBackup}
          className="w-full h-11 border border-amber-400/40 text-amber-400 hover:bg-amber-400/10 text-sm flex items-center justify-center gap-2 transition-colors">
          <RotateCcw size={15} /> Annuler l'import — restaurer le programme d'avant ({backup.count} exercices, {new Date(backup.created_at).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" })})
        </button>
      )}
      <div className="bg-[#14141A] border border-[#2E2E3A]">
        <p className="px-3 pt-2 text-[10px] text-[#666]">Glisse-dépose les exercices pour changer l'ordre</p>
        <Reorder.Group axis="y" values={rows} onReorder={setRows} as="div"
          className="divide-y divide-white/5" data-testid="admin-program-list">
          {rows.map((r) => (
            <Reorder.Item key={r.id} value={r} as="div" onDragEnd={persistOrder}
              className="px-3 py-2.5 flex items-center gap-2 bg-[#14141A] cursor-grab active:cursor-grabbing"
              data-testid={`program-row-${r.id}`}>
              <GripVertical size={15} className="text-[#555] shrink-0" />
              <span className="text-[#555] font-mono text-xs w-5">{r.ordre}</span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm truncate">{r.exercice}</p>
                <p className="text-[#B0B0B4] text-[10px]">{r.groupe} · {r.muscle} · {r.series} séries · repos {r.repos_s}s</p>
              </div>
              <button data-testid={`edit-row-${r.id}`} onClick={() => setEditing(r)}
                className="text-[#B0B0B4] hover:text-white p-2 transition-colors"><Pencil size={15} /></button>
              <button data-testid={`delete-row-${r.id}`} onClick={() => remove(r.id)}
                className="text-[#555] hover:text-[#FF3B30] p-2 transition-colors"><Trash2 size={15} /></button>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      </div>
      {editing && <RowForm initial={editing} onSave={save} onClose={() => setEditing(null)} blocs={blocs} />}
      {dup && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setDup(null)} />
          <div data-testid="duplicate-form" className="relative w-full max-w-md bg-[#14141A] border border-white/15 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-3xl text-white">DUPLIQUER {jour.toUpperCase()} · BLOC {bloc}</h2>
              <button data-testid="duplicate-close" onClick={() => setDup(null)} className="text-[#B0B0B4] hover:text-white p-2"><X size={18} /></button>
            </div>
            <p className="text-[#B0B0B4] text-xs mb-3">Copie tous les exercices de ce jour vers la cible (ajoutés à la suite des exercices existants) — pratique pour créer une variante.</p>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Bloc cible">
                {dup.newBloc ? (
                  <input data-testid="duplicate-new-bloc" className="auth-input" placeholder="C" maxLength={3} autoFocus
                    value={dup.target_bloc} onChange={(e) => setDup({ ...dup, target_bloc: e.target.value.toUpperCase() })} />
                ) : (
                  <select data-testid="duplicate-target-bloc" className="auth-input" value={dup.target_bloc}
                    onChange={(e) => {
                      if (e.target.value === "__new__") setDup({ ...dup, newBloc: true, target_bloc: "" });
                      else setDup({ ...dup, target_bloc: e.target.value });
                    }}>
                    {blocs.map((b) => <option key={b.bloc} value={b.bloc}>{b.bloc}{b.label ? ` · ${b.label}` : ""}</option>)}
                    <option value="__new__">➕ Nouveau bloc…</option>
                  </select>
                )}
              </Field>
              <Field label="Séance cible">
                <select data-testid="duplicate-target-jour" className="auth-input" value={dup.target_jour}
                  onChange={(e) => setDup({ ...dup, target_jour: e.target.value })}>
                  {JOURS.map((j) => <option key={j}>{j}</option>)}
                </select>
              </Field>
            </div>
            <button data-testid="duplicate-confirm" onClick={duplicateDay}
              className="w-full h-12 mt-4 bg-[#FF3B30] text-white font-display text-2xl tracking-wide hover:bg-[#e0322a] transition-colors">
              DUPLIQUER
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Members = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [tempPw, setTempPw] = useState(null);
  const load = useCallback(() => api.get("/admin/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => { load(); }, [load]);

  const resetPassword = async (u) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${u.name} ? Un mot de passe temporaire sera généré et il devra le changer à sa prochaine connexion.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setTempPw(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Supprimer ${u.name} (${u.email}) et toutes ses données ?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.name} supprimé`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleRole = async (u) => {
    const newRole = u.role === "admin" ? "membre" : "admin";
    const msg = newRole === "admin"
      ? `Donner l'accès Admin à ${u.name} ? Il verra le bouton Admin dans son menu.`
      : `Retirer l'accès Admin à ${u.name} ?`;
    if (!window.confirm(msg)) return;
    try {
      await api.put(`/admin/users/${u.id}/role`, { role: newRole });
      toast.success(newRole === "admin" ? `${u.name} est maintenant admin 👑` : `${u.name} est redevenu membre`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4">
      {tempPw && <TempPasswordModal result={tempPw} onClose={() => setTempPw(null)} />}
      <InviteCodesSection />
      <div className="bg-[#14141A] border border-[#2E2E3A] divide-y divide-white/5" data-testid="members-list">
      {users.map((u) => (
        <div key={u.id} className="px-4 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate flex items-center gap-1.5">
              {u.name}
              {u.role === "admin" && <Crown size={13} className="text-amber-400 shrink-0" />}
            </p>
            <p className="text-[#B0B0B4] text-xs truncate">{u.email} · {u.nb_series} séries</p>
          </div>
          <button data-testid={`reset-password-${u.id}`} onClick={() => resetPassword(u)}
            title="Réinitialiser le mot de passe"
            className="text-[#555] hover:text-white p-2 transition-colors"><KeyRound size={15} /></button>
          {u.id !== user.id && (
            <>
              <button data-testid={`toggle-role-${u.id}`} onClick={() => toggleRole(u)}
                title={u.role === "admin" ? "Retirer l'accès admin" : "Promouvoir admin"}
                className={`p-2 transition-colors ${u.role === "admin" ? "text-amber-400 hover:text-[#B0B0B4]" : "text-[#555] hover:text-amber-400"}`}>
                <Crown size={15} />
              </button>
              <button data-testid={`delete-user-${u.id}`} onClick={() => remove(u)}
                className="text-[#555] hover:text-[#FF3B30] p-2 transition-colors"><Trash2 size={15} /></button>
            </>
          )}
        </div>
      ))}
      </div>
    </div>
  );
};

const ExerciseBank = () => {
  const [bank, setBank] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");
  const [nf, setNf] = useState({ exercice: "", muscle: "", equipement: "" });
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const load = useCallback(() => api.get("/exercises/bank").then(({ data }) => setBank(data)), []);
  useEffect(() => { load(); }, [load]);

  const seedPictos = async () => {
    if (!window.confirm("Ajouter à la banque tous les exercices des 227 pictogrammes qui n'y sont pas encore ? (muscle et équipement devinés automatiquement, modifiables ensuite)")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/exercises/bank/seed-pictos");
      toast.success(data.added > 0
        ? `${data.added} exercices ajoutés — la banque en compte maintenant ${data.total}`
        : "Aucun exercice manquant — la banque est déjà complète");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSeeding(false);
  };

  const norm = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const filtered = search.trim()
    ? bank.filter((b) => norm(`${b.exercice} ${b.muscle} ${b.equipement}`).includes(norm(search)))
    : bank;

  const add = async () => {
    if (!nf.exercice || !nf.muscle) { toast.error("Nom et muscle requis"); return; }
    try {
      await api.post("/admin/exercises/bank", { ...nf, equipement: nf.equipement || "Aucun" });
      toast.success(`${nf.exercice} ajouté à la banque`);
      setNf({ exercice: "", muscle: "", equipement: "" });
      setShowAdd(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Retirer « ${b.exercice} » de la banque ?`)) return;
    await api.delete(`/admin/exercises/bank/${b.id}`);
    load();
  };

  const saveEdit = async () => {
    if (!editing.exercice || !editing.muscle) { toast.error("Nom et muscle requis"); return; }
    try {
      await api.put(`/admin/exercises/bank/${editing.id}`, {
        exercice: editing.exercice, muscle: editing.muscle,
        equipement: editing.equipement || "Aucun", video_url: editing.video_url || "",
      });
      toast.success("Exercice modifié");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const muscles = [...new Set(filtered.map((b) => b.muscle))];
  return (
    <div className="space-y-3" data-testid="exercise-bank">
      <p className="text-[#B0B0B4] text-xs">{bank.length} exercices dans la banque — utilisés comme menu déroulant partout dans la gestion du programme.</p>
      {bank.length < 200 && (
        <button data-testid="bank-seed-pictos-button" disabled={seeding} onClick={seedPictos}
          className="w-full h-11 border border-[#FF3B30]/50 text-[#FF3B30] text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#FF3B30]/10 transition-colors disabled:opacity-40">
          <Plus size={15} /> {seeding ? "Import en cours..." : "Importer les exercices des 227 pictogrammes"}
        </button>
      )}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
        <input data-testid="bank-search-input" className="auth-input pl-9" placeholder="Rechercher un exercice, muscle, équipement…"
          value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {search.trim() && (
        <p data-testid="bank-search-count" className="text-[#B0B0B4] text-[11px]">{filtered.length} résultat{filtered.length > 1 ? "s" : ""}</p>
      )}
      <button data-testid="bank-add-button" onClick={() => setShowAdd(!showAdd)}
        className="w-full h-11 border border-dashed border-white/20 text-[#B0B0B4] hover:text-white hover:border-[#FF3B30] text-sm flex items-center justify-center gap-2 transition-colors">
        {showAdd ? <X size={16} /> : <Plus size={16} />} {showAdd ? "Annuler" : "Ajouter un exercice à la banque"}
      </button>
      {showAdd && (
        <div data-testid="bank-add-form" className="bg-[#14141A] border border-[#FF3B30]/40 p-3 space-y-2">
          <input data-testid="bank-exercice-input" className="auth-input" placeholder="Nom de l'exercice"
            value={nf.exercice} onChange={(e) => setNf({ ...nf, exercice: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="bank-muscle-input" className="auth-input" placeholder="Muscle (ex: Pectoraux)" list="muscles-list"
              value={nf.muscle} onChange={(e) => setNf({ ...nf, muscle: e.target.value })} />
            <datalist id="muscles-list">{muscles.map((m) => <option key={m} value={m} />)}</datalist>
            <input data-testid="bank-equipement-input" className="auth-input" placeholder="Équipement (ex: Barre)"
              value={nf.equipement} onChange={(e) => setNf({ ...nf, equipement: e.target.value })} />
          </div>
          <button data-testid="bank-save-button" onClick={add}
            className="w-full h-10 bg-[#FF3B30] text-white font-display text-lg tracking-wide hover:bg-[#e0322a] transition-colors">
            AJOUTER À LA BANQUE
          </button>
        </div>
      )}
      {muscles.map((m) => (
        <section key={m}>
          <h3 className="font-display text-xl text-white mb-1">{m.toUpperCase()} <span className="text-[#B0B0B4] text-xs font-sans">({filtered.filter((b) => b.muscle === m).length})</span></h3>
          <div className="bg-[#14141A] border border-[#2E2E3A] divide-y divide-white/5">
            {filtered.filter((b) => b.muscle === m).map((b) => (
              editing?.id === b.id ? (
                <div key={b.id} data-testid={`bank-edit-form-${b.id}`} className="p-3 space-y-2 bg-[#14141A]">
                  <input data-testid="bank-edit-exercice" className="auth-input" placeholder="Nom"
                    value={editing.exercice} onChange={(e) => setEditing({ ...editing, exercice: e.target.value })} />
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="bank-edit-muscle" className="auth-input" placeholder="Muscle"
                      value={editing.muscle} onChange={(e) => setEditing({ ...editing, muscle: e.target.value })} />
                    <input data-testid="bank-edit-equipement" className="auth-input" placeholder="Équipement"
                      value={editing.equipement} onChange={(e) => setEditing({ ...editing, equipement: e.target.value })} />
                  </div>
                  <input data-testid="bank-edit-video" className="auth-input" placeholder="Lien vidéo personnalisé (YouTube…) — vide = recherche auto"
                    value={editing.video_url || ""} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} />
                  <div className="flex gap-2">
                    <button data-testid="bank-edit-save" onClick={saveEdit}
                      className="flex-1 h-9 bg-[#FF3B30] text-white font-display text-base tracking-wide hover:bg-[#e0322a] transition-colors">ENREGISTRER</button>
                    <button data-testid="bank-edit-cancel" onClick={() => setEditing(null)}
                      className="h-9 px-3 border border-white/15 text-[#B0B0B4] hover:text-white text-xs transition-colors">Annuler</button>
                  </div>
                </div>
              ) : (
              <div key={b.id} data-testid={`bank-row-${b.id}`} className="px-3 py-2 flex items-center gap-3">
                {b.picto_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${b.picto_url}`} alt={b.exercice}
                  className="w-10 h-10 object-cover bg-white shrink-0 border border-[#2E2E3A]" />}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{b.exercice}</p>
                  <p className="text-[#B0B0B4] text-[10px]">{b.equipement}{b.emplacements ? ` · ${b.emplacements}` : ""}</p>
                </div>
                {b.video_url && (
                  <a href={b.video_url} target="_blank" rel="noreferrer"
                    className="text-[#B0B0B4] hover:text-[#FF3B30] p-1.5 transition-colors"><Youtube size={14} /></a>
                )}
                <button data-testid={`bank-edit-${b.id}`} onClick={() => setEditing({ ...b, video_url: b.custom_video ? b.video_url : "" })}
                  className="text-[#555] hover:text-white p-1.5 transition-colors"><Pencil size={14} /></button>
                <button data-testid={`bank-delete-${b.id}`} onClick={() => remove(b)}
                  className="text-[#555] hover:text-[#FF3B30] p-1.5 transition-colors"><Trash2 size={14} /></button>
              </div>
              )
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

const TABS = [
  { id: "bord", label: "BORD", C: Dashboard },
  { id: "inviter", label: "INVITER", C: Invites },
  { id: "programme", label: "PROGRAMME", C: ProgramManager },
  { id: "banque", label: "BANQUE", C: ExerciseBank },
  { id: "membres", label: "MEMBRES", C: Members },
];

export default function AdminPage() {
  const [tab, setTab] = useState("bord");
  const Active = TABS.find((t) => t.id === tab).C;
  return (
    <div className="pb-28">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/85 border-b border-[#2E2E3A] px-4 pt-4 pb-3">
        <h1 className="font-display text-4xl text-white leading-none">ADMIN</h1>
        <p className="text-[#B0B0B4] text-xs mt-0.5">Contrôle total — invitations, programme, membres</p>
        {/* flex-wrap plutot qu'un defilement horizontal : sous 500px les cinq
            onglets font 467px et le dernier etait coupe en deux. La barre
            defilait deja, mais no-scrollbar masquait toute indication — rien
            ne laissait deviner qu'il fallait faire glisser. Au-dessus de
            500px la rangee reste sur une seule ligne. */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`h-9 px-3.5 text-xs uppercase tracking-wider whitespace-nowrap border transition-colors ${
                tab === t.id ? "border-[#FF3B30] text-white bg-[#FF3B30]/10" : "border-[#2E2E3A] text-[#B0B0B4] hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="px-4 mt-4">
        <Active />
      </main>
    </div>
  );
}
